import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  Gift,
  Users,
  Clock,
  Loader2,
  Play,
  Shuffle,
  Lock,
  ArrowRight,
  RefreshCw,
  Crown,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

interface WhiteElephantGameProps {
  onNavigate: (screen: string, data?: { giftGroupId?: string; tab?: string }) => void;
  giftGroupId: string | null;
}

interface GameState {
  giftGroupId: string;
  gameId?: string;
  status: 'not_started' | 'order_generated' | 'in_progress' | 'completed';
  pickOrder: Array<{
    userId: string;
    name: string;
    order: number;
    isCurrent?: boolean;
  }>;
  currentPickerIndex: number | null;
  currentPickerUserId: string | null;
  currentPickerName: string | null;
  isYourTurn: boolean;
  pickTimeRemaining: number | null;
  pickTimeoutSeconds: number;
  isFirstPickerFinalTurn: boolean;
  lastStolenGiftId: number | null;
  unopenedGifts: Array<{
    giftId: number;
    giftName: string;
    giftDescription: string;
    giftPrice: number;
    giftImageUrl: string;
    suggestedByUserId: string;
    suggestedByName: string;
  }>;
  openedGifts: Array<{
    pickId: string;
    giftId: number;
    giftName: string;
    giftDescription: string;
    giftPrice: number;
    giftImageUrl: string;
    currentOwnerUserId: string;
    currentOwnerName: string;
    stealCount: number;
    canBeStolen: boolean;
    suggestedByUserId: string;
    suggestedByName: string;
  }>;
  frozenGifts: Array<{
    pickId: string;
    giftId: number;
    giftName: string;
    giftDescription: string;
    giftPrice: number;
    giftImageUrl: string;
    currentOwnerUserId: string;
    currentOwnerName: string;
    stealCount: number;
    suggestedByUserId: string;
    suggestedByName: string;
  }>;
  canSteal: number[];
  canPick: number[];
  turnHistory: Array<{
    turnNumber: number;
    actionType: 'pick' | 'steal';
    actorUserId: string;
    actorName: string;
    previousOwnerUserId?: string;
    previousOwnerName?: string;
    giftName: string;
    createdAt: string;
  }>;
}

export function WhiteElephantGame({ onNavigate, giftGroupId }: WhiteElephantGameProps) {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const loadGameState = useCallback(async () => {
    if (!giftGroupId) return;
    
    try {
      const state = await api.getWhiteElephantGameState(giftGroupId);
      setGameState(state);
      setTimeRemaining(state.pickTimeRemaining);
    } catch (error) {
      console.error('Failed to load game state:', error);
      toast.error('Failed to load game state');
    } finally {
      setLoading(false);
    }
  }, [giftGroupId]);

  const loadCurrentUser = useCallback(async () => {
    try {
      const user = await api.getCurrentUser();
      setCurrentUserId(user.id);
    } catch (error) {
      console.error('Failed to load current user:', error);
    }
  }, []);

  useEffect(() => {
    loadCurrentUser();
  }, [loadCurrentUser]);

  useEffect(() => {
    loadGameState();
  }, [loadGameState]);

  // Separate effect for polling - uses gameState.status from state
  useEffect(() => {
    // Only poll when game is in progress
    if (gameState?.status !== 'in_progress') {
      return;
    }

    const pollInterval = setInterval(loadGameState, 5000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [loadGameState, gameState?.status]);

  // Countdown timer
  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  const handleGenerateOrder = async () => {
    if (!giftGroupId) return;

    try {
      setActionLoading(true);
      await api.generateWhiteElephantPickOrder(giftGroupId);
      toast.success('Pick order generated! Share with all participants.');
      await loadGameState();
    } catch (error: any) {
      console.error('Failed to generate pick order:', error);
      toast.error(error.message || 'Failed to generate pick order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartGame = async () => {
    if (!giftGroupId) return;

    try {
      setActionLoading(true);
      await api.startWhiteElephantGame(giftGroupId);
      toast.success('Game started! First picker, make your choice.');
      await loadGameState();
    } catch (error: any) {
      console.error('Failed to start game:', error);
      toast.error(error.message || 'Failed to start game');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePick = async (giftId: number) => {
    if (!giftGroupId) return;

    try {
      setActionLoading(true);
      const result = await api.whiteElephantPick(giftGroupId, giftId);
      toast.success(`You picked "${result.giftName}"!`);
      if (result.gameComplete) {
        toast.success('🎉 Game Over! All gifts have been claimed.');
      }
      await loadGameState();
    } catch (error: any) {
      console.error('Failed to pick gift:', error);
      toast.error(error.message || 'Failed to pick gift');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSteal = async (giftId: number) => {
    if (!giftGroupId) return;

    try {
      setActionLoading(true);
      const result = await api.whiteElephantSteal(giftGroupId, giftId);
      toast.success(`You stole "${result.giftName}"! ${result.isFrozen ? '🔒 This gift is now frozen.' : ''}`);
      await loadGameState();
    } catch (error: any) {
      console.error('Failed to steal gift:', error);
      toast.error(error.message || 'Failed to steal gift');
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!giftGroupId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="p-6 text-center">
          <h2 className="text-xl mb-2">No Gift Group Selected</h2>
          <p className="text-muted-foreground mb-4">Please select a White Elephant gift group first</p>
          <Button onClick={() => onNavigate('dashboard', { tab: 'todo' })}>Go to Dashboard</Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="p-6 text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl mb-2">Failed to Load Game</h2>
          <p className="text-muted-foreground mb-4">Could not load the White Elephant game state</p>
          <Button onClick={() => loadGameState()}>Try Again</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <Card className="p-6 border-2 border-primary/20 shadow-lg">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-primary mb-2">🎁 White Elephant Game</h1>
              <div className="flex items-center gap-2">
                <Badge variant={
                  gameState.status === 'completed' ? 'default' :
                  gameState.status === 'in_progress' ? 'destructive' :
                  gameState.status === 'order_generated' ? 'secondary' : 'outline'
                }>
                  {gameState.status === 'not_started' && 'Not Started'}
                  {gameState.status === 'order_generated' && 'Order Generated - Ready to Start'}
                  {gameState.status === 'in_progress' && 'Game In Progress'}
                  {gameState.status === 'completed' && '🎉 Game Complete'}
                </Badge>
                {gameState.isFirstPickerFinalTurn && gameState.status === 'in_progress' && (
                  <Badge variant="outline" className="bg-yellow-50 border-yellow-500 text-yellow-700">
                    <Crown className="w-3 h-3 mr-1" />
                    First Picker's Final Turn
                  </Badge>
                )}
              </div>
            </div>
            <Button variant="outline" onClick={() => onNavigate('gift-group-overview', { giftGroupId })}>
              Back to Overview
            </Button>
          </div>
        </Card>

        {/* Timer Card (when in progress) */}
        {gameState.status === 'in_progress' && gameState.isYourTurn && timeRemaining !== null && (
          <Card className={`p-6 border-2 ${timeRemaining < 60 ? 'border-red-500 bg-red-50' : 'border-green-500 bg-green-50'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className={`w-8 h-8 ${timeRemaining < 60 ? 'text-red-500' : 'text-green-600'}`} />
                <div>
                  <h3 className="text-lg font-bold">It's Your Turn!</h3>
                  <p className="text-sm text-muted-foreground">Pick an unopened gift or steal an opened one</p>
                </div>
              </div>
              <div className={`text-4xl font-mono font-bold ${timeRemaining < 60 ? 'text-red-600' : 'text-green-600'}`}>
                {formatTime(timeRemaining)}
              </div>
            </div>
          </Card>
        )}

        {/* Current Picker Info (when not your turn) */}
        {gameState.status === 'in_progress' && !gameState.isYourTurn && gameState.currentPickerName && (
          <Card className="p-6 border-2 border-blue-200 bg-blue-50">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="text-lg font-semibold">
                  {gameState.currentPickerName}'s Turn
                </h3>
                <p className="text-sm text-muted-foreground">Waiting for them to pick or steal...</p>
              </div>
              <Loader2 className="w-5 h-5 animate-spin text-blue-600 ml-auto" />
            </div>
          </Card>
        )}

        {/* Admin Controls */}
        {gameState.status === 'not_started' && (
          <Card className="p-6 border-2 border-primary/20">
            <h3 className="text-lg font-semibold mb-4">Start the Game</h3>
            <p className="text-muted-foreground mb-4">
              Generate the random pick order to share with all participants. 
              This should be done about 1 hour before the event.
            </p>
            <Button onClick={handleGenerateOrder} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shuffle className="w-4 h-4 mr-2" />}
              Generate Pick Order
            </Button>
          </Card>
        )}

        {gameState.status === 'order_generated' && (
          <Card className="p-6 border-2 border-green-200 bg-green-50">
            <h3 className="text-lg font-semibold mb-4 text-green-800">Pick Order Ready!</h3>
            <p className="text-muted-foreground mb-4">
              The pick order has been generated. When everyone is ready, start the game.
            </p>
            <Button onClick={handleStartGame} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Start Game
            </Button>
          </Card>
        )}

        {/* Pick Order */}
        {gameState.pickOrder.length > 0 && (
          <Card className="p-6 border-2 border-primary/20">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Pick Order
            </h3>
            <div className="flex flex-wrap gap-2">
              {gameState.pickOrder.map((picker, index) => (
                <Badge 
                  key={picker.userId}
                  variant={picker.isCurrent ? 'default' : 'outline'}
                  className={`text-sm py-1 px-3 ${
                    picker.isCurrent 
                      ? 'bg-primary text-white' 
                      : picker.userId === currentUserId 
                        ? 'border-primary text-primary' 
                        : ''
                  }`}
                >
                  #{index + 1} {picker.name}
                  {picker.isCurrent && <ArrowRight className="w-3 h-3 ml-1 inline" />}
                  {picker.userId === currentUserId && ' (You)'}
                </Badge>
              ))}
            </div>
          </Card>
        )}

        {/* Game Board */}
        {(gameState.status === 'in_progress' || gameState.status === 'completed') && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Unopened Gifts */}
            <Card className="p-6 border-2 border-gray-200">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Gift className="w-5 h-5 text-gray-500" />
                Unopened Gifts ({gameState.unopenedGifts.length})
              </h3>
              {gameState.unopenedGifts.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">All gifts have been opened!</p>
              ) : (
                <div className="space-y-3">
                  {gameState.unopenedGifts.map((gift) => (
                    <Card key={gift.giftId} className="p-4 border hover:border-primary/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-600">🎁 Mystery Gift</p>
                          <p className="text-sm text-muted-foreground">by {gift.suggestedByName}</p>
                        </div>
                        {gameState.isYourTurn && gameState.canPick.includes(gift.giftId) && (
                          <Button 
                            size="sm" 
                            onClick={() => handlePick(gift.giftId)}
                            disabled={actionLoading}
                          >
                            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pick'}
                          </Button>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>

            {/* Opened Gifts */}
            <Card className="p-6 border-2 border-green-200">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Opened Gifts ({gameState.openedGifts.length + gameState.frozenGifts.length})
              </h3>
              {gameState.openedGifts.length === 0 && gameState.frozenGifts.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No gifts opened yet</p>
              ) : (
                <div className="space-y-3">
                  {/* Frozen Gifts First */}
                  {gameState.frozenGifts.map((gift) => (
                    <Card key={gift.pickId} className="p-4 border-2 border-blue-300 bg-blue-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-blue-600" />
                            <p className="font-medium">{gift.giftName}</p>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">${gift.giftPrice.toFixed(2)}</p>
                          <p className="text-sm text-blue-600 mt-1">
                            Owned by: {gift.currentOwnerName}
                          </p>
                          <Badge variant="secondary" className="mt-2 bg-blue-100 text-blue-700">
                            🔒 Frozen (stolen 3 times)
                          </Badge>
                        </div>
                      </div>
                    </Card>
                  ))}
                  
                  {/* Opened (stealable) Gifts */}
                  {gameState.openedGifts.map((gift) => (
                    <Card key={gift.pickId} className={`p-4 border ${
                      gift.currentOwnerUserId === currentUserId 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200'
                    }`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="font-medium">{gift.giftName}</p>
                          <p className="text-sm text-muted-foreground mt-1">${gift.giftPrice.toFixed(2)}</p>
                          <p className="text-sm text-green-600 mt-1">
                            Owned by: {gift.currentOwnerName}
                            {gift.currentOwnerUserId === currentUserId && ' (You!)'}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {gift.stealCount}/3 steals
                            </Badge>
                            {gift.canBeStolen && (
                              <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                                Can be stolen
                              </Badge>
                            )}
                          </div>
                        </div>
                        {gameState.isYourTurn && 
                         gameState.canSteal.includes(gift.giftId) && 
                         gift.currentOwnerUserId !== currentUserId && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-orange-500 text-orange-600 hover:bg-orange-50"
                            onClick={() => handleSteal(gift.giftId)}
                            disabled={actionLoading}
                          >
                            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Steal'}
                          </Button>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Turn History */}
        {gameState.turnHistory.length > 0 && (
          <Card className="p-6 border-2 border-primary/20">
            <h3 className="text-lg font-semibold mb-4">Game History</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {gameState.turnHistory.slice().reverse().map((turn, index) => (
                <div key={index} className="flex items-center gap-2 text-sm py-2 border-b last:border-0">
                  <Badge variant="outline" className="w-8 text-center">
                    #{turn.turnNumber}
                  </Badge>
                  <span>
                    <strong>{turn.actorName}</strong>
                    {turn.actionType === 'pick' ? (
                      <> picked <strong>{turn.giftName}</strong></>
                    ) : (
                      <> stole <strong>{turn.giftName}</strong> from {turn.previousOwnerName}</>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Game Complete Summary */}
        {gameState.status === 'completed' && (
          <Card className="p-6 border-2 border-green-500 bg-green-50">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-green-700 mb-4">🎉 Game Complete!</h2>
              <p className="text-muted-foreground mb-6">
                All gifts have been claimed. Here's who got what:
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                {[...gameState.openedGifts, ...gameState.frozenGifts].map((gift) => (
                  <Card key={gift.pickId} className="p-4 bg-white">
                    <p className="font-medium">{gift.currentOwnerName}</p>
                    <p className="text-sm text-muted-foreground">{gift.giftName}</p>
                    <p className="text-sm text-green-600">${gift.giftPrice.toFixed(2)}</p>
                  </Card>
                ))}
              </div>
              <Button 
                className="mt-6"
                onClick={() => onNavigate('gift-group-overview', { giftGroupId })}
              >
                Back to Gift Group
              </Button>
            </div>
          </Card>
        )}

        {/* Refresh Button */}
        <div className="flex justify-center">
          <Button variant="outline" onClick={loadGameState} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh Game State
          </Button>
        </div>
      </div>
    </div>
  );
}
